<template>
    <v-container class="property-detail" v-if="property.property_units">
        <span itemscope itemtype="https://schema.org/RentAction">
            <meta itemprop="name" :content="metaData.title" />
            <meta itemprop="description" :content="metaData.description" />
            <meta itemprop="image" :content="metaData.image" />
            <meta itemprop="location" :content="metaData.location" />
            <meta itemprop="min_price" :content="property.min_price" />
            <meta itemprop="max_price" :content="property.max_price" />
            <meta itemprop="priceCurrency" :content="metaData.currency" />
            <meta itemprop="url" :content="metaData.url" />
        </span>
        <v-row class="mb-4">
            <v-col
                :cols="
                    $vuetify.breakpoint.lgAndUp &&
                    $vuetify.breakpoint.height > 800
                        ? '8'
                        : '12'
                "
                class="property-photos"
            >
                <div class="extra">
                    <v-btn
                        v-if="property.matterport_url"
                        depressed
                        color="rgba(255,255,255,0.5)"
                        class="btn-edurent font-weight-regular"
                        @click="showVirtualTour = true"
                    >
                        <v-icon class="mr-2">
                            mdi-rotate-3d-variant
                        </v-icon>
                        Virtual Tour
                    </v-btn>
                    <v-btn
                        depressed
                        color="rgba(255,255,255,0.5)"
                        class="btn-edurent font-weight-regular"
                        @click="handleLike"
                        @mouseover="hoverLike = true"
                        @mouseleave="hoverLike = false"
                    >
                        <v-icon class="mr-2" :color="color">
                            {{ favoriteIcon }}
                        </v-icon>
                        Save
                    </v-btn>
                </div>
                <v-carousel
                    :height="$vuetify.breakpoint.mdAndUp ? '600' : '400'"
                    hide-delimiter-background
                    show-arrows-on-hover
                    hide-delimiters
                    :class="$vuetify.breakpoint.smAndUp ? '' : 'no-rounded'"
                >
                    <v-carousel-item
                        v-for="(propertyImage, i) in propertyImages"
                        :key="i"
                    >
                        <v-row
                            class="full-height"
                            align="center"
                            justify="center"
                        >
                            <v-img
                                :src="propertyImage.image_url"
                                class="property-image"
                                :alt="property.name"
                                :height="
                                    $vuetify.breakpoint.mdAndUp
                                        ? '600px'
                                        : '400px'
                                "
                                width="250"
                                contained
                            ></v-img>
                        </v-row>
                    </v-carousel-item>
                </v-carousel>
            </v-col>
        </v-row>

        <!-- Virtual Tour Dialog -->
        <v-dialog
            v-model="showVirtualTour"
            fullscreen
            transition="dialog-bottom-transition"
        >
            <v-card class="virtual-tour-dialog">
                <div class="virtual-tour-header">
                    <v-btn icon small dark @click="showVirtualTour = false">
                        <v-icon size="20">mdi-close</v-icon>
                    </v-btn>
                    <span class="virtual-tour-title">
                        <v-icon size="16" class="mr-1">mdi-rotate-3d-variant</v-icon>
                        {{ property.name }}
                    </span>
                    <v-btn
                        icon
                        small
                        dark
                        :href="property.matterport_url"
                        target="_blank"
                        title="Open in new tab"
                    >
                        <v-icon size="18">mdi-open-in-new</v-icon>
                    </v-btn>
                </div>
                <div class="virtual-tour-iframe-container">
                    <iframe
                        v-if="showVirtualTour"
                        :src="property.matterport_url"
                        width="100%"
                        height="100%"
                        frameborder="0"
                        allowfullscreen
                        allow="xr-spatial-tracking"
                    ></iframe>
                </div>
            </v-card>
        </v-dialog>

        <v-row>
            <v-col cols="12" md="8" class="pt-0 pt-sm-4">
                <!-- 1. Breadcrumb Navigation (First, subtle) -->
                <v-row class="breadcrumb-section px-0 pb-3" v-if="property.location_hierarchy">
                    <v-col cols="12" class="py-0">
                        <LocationBreadcrumb :location-hierarchy="property.location_hierarchy" />
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12" class="py-1">
                        <div>
                            <v-row class="d-flex justify-space-between properties-name-price pb-3 px-2">
                                <h1 class="property-details-title" :style="$vuetify.breakpoint.xs ? 'font-size: 25px;' : ''">{{ property.name ? property.name : '' }}</h1>
                                <p :style="$vuetify.breakpoint.xs ? 'font-size: 25px;' : ''">From ${{ minPrice }} to ${{ maxPrice }} <span class="price-label">{{ property.housing_type === 'shared' ? 'per bed' : 'per unit' }}</span></p>
                            </v-row>
                            <v-row class="properties-details">
                                <v-col
                                    cols="12"
                                    class="py-0 address-text d-flex flex-column pb-6"
                                >
                                    <span>{{ property.address_line_1 }}</span>
                                    <span>{{ property.city }}{{ property.state || property.state_id ? ', ' : '' }}{{ property.state ? property.state.abbrev : '' }} {{ property.zip_code }}</span>
                                    <span class="d-none">{{ property.country }}</span>
                                </v-col>
                            </v-row>
                        </div>
                        <v-spacer></v-spacer>
                        <div class="properties-icons d-flex pb-2">
                            <span class="d-flex align-center mr-4">
                                <v-img
                                    alt="Bedrooms"
                                    width="18"
                                    height="18"
                                    contain
                                    src="@/assets/icons/icon-bed.svg"
                                    class="mr-2"
                                ></v-img>
                                {{ numBedsLabel }} bds
                            </span>
                            <span class="d-flex align-center">
                                <v-img
                                    alt="Bathrooms"
                                    width="18"
                                    height="18"
                                    contain
                                    src="@/assets/icons/icon-bath.svg"
                                    class="mr-2"
                                ></v-img>
                                {{ numBathslabel }} ba
                            </span>
                        </div>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12">
                        <v-card
                            elevation="0"
                            color="#f4f5f6"
                            class="rent-specials-box py-6"
                        >
                            <v-btn
                                color="primary"
                                depressed
                                class="btn-edurent"
                                width="150"
                            >
                                Rent Specials
                            </v-btn>
                            <div class="pl-6 pt-6" v-if="property.active_property_rent_specials && property.active_property_rent_specials.length > 0">
                                <div
                                    v-for="special in property.active_property_rent_specials"
                                    :key="special.id"
                                    class="rent-special-item mb-4"
                                >
                                    <strong class="rent-special-offer">{{ special.formatted_offer || special.rent_special.label }}</strong>
                                    <p v-if="special.rent_special.content" class="rent-special-description mb-0">
                                        {{ special.rent_special.content }}
                                    </p>
                                </div>
                            </div>
                            <p v-else class="pl-6 pt-6">
                                No rent specials yet for this property.
                            </p>
                        </v-card>
                    </v-col>
                </v-row>
                <v-row class="mt-8 availability-box">
                    <v-col cols="12">
                        <h3>Availability</h3>

                        <v-skeleton-loader
                            class="mx-auto"
                            type="list-item-avatar-two-line"
                            tile
                            loading
                            v-if="!propertyUnitsByBeds"
                        ></v-skeleton-loader>

                        <v-skeleton-loader
                            class="mx-auto"
                            type="list-item-avatar-two-line"
                            tile
                            loading
                            v-if="!propertyUnitsByBeds"
                        ></v-skeleton-loader>

                        <v-skeleton-loader
                            class="mx-auto"
                            type="list-item-avatar-two-line"
                            tile
                            loading
                            v-if="!propertyUnitsByBeds"
                        ></v-skeleton-loader>

                        <v-tabs
                            class="properties-tabs pt-4"
                            slider-size="3"
                            :grow="$vuetify.breakpoint.smAndDown"
                            :centered="$vuetify.breakpoint.smAndDown"
                            height="60"
                        >
                            <v-tab
                                v-for="(units, index) in propertyUnitsByBeds"
                                :key="index"
                            >
                                {{ index === 0 ? "All" : index }}
                                {{
                                    index !== 0 ? "bed" : ""
                                }}{{ Number(index) > 1 ? "s" : "" }}
                            </v-tab>

                            <v-tab-item
                                class="row-availability pt-4"
                                v-for="(units, index) in propertyUnitsByBeds"
                                :key="index"
                            >
                                <span
                                    v-if="
                                        propertyUnitsByBeds[index] === undefined
                                    "
                                    class="no-property d-flex justify-center align-center grey--text lighten-5"
                                >
                                    There is no property for
                                    {{ index === 0 ? "All" : index }}
                                    {{
                                        index !== 0 ? "bed" : ""
                                    }}{{ Number(index) > 1 ? "s" : "" }}
                                </span>
                                <PropertyUnitBlock
                                    v-on:applyTo="goToApplicationWebsite"
                                    v-on:joinWaitlist="joinWaitlist"
                                    v-on:removeFromWaitlist="removeFromWaitlist"
                                    v-for="unit in units"
                                    :key="unit.id"
                                    :property-unit="unit"
                                    :is-waitlist-loading="
                                        waitlistLoadingUnitId === unit.id
                                    "
                                    :housing-type="property.housing_type"
                                />
                            </v-tab-item>
                        </v-tabs>
                    </v-col>
                </v-row>

                <v-row class="mt-4">
                    <v-col class="pb-0">
                        <h3>Roommates?</h3>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col class="pb-0">
                        <p>
                            These are students who favorited or contacted this
                            place and also need a roommate.
                        </p>
                    </v-col>
                </v-row>
                <v-row class="pr-0">
                    <v-col
                        cols="12"
                        md="6"
                        v-for="(n, i) in previewCount"
                        :key="i"
                        class="justify-space-between"
                    >
                        <RoommateCard
                            :roommate="usersWhoLikedPropertyUnit[i]"
                            v-if="usersWhoLikedPropertyUnit[i]"
                        />
                    </v-col>
                </v-row>

                <v-row v-if="showUsersWhoLikedPropertyUnit">
                    <v-col
                        cols="12"
                        md="6"
                        v-for="(n, index) in usersWhoLikedPropertyUnit.length"
                        :key="index + previewCount"
                    >
                        <RoommateCard
                            :roommate="
                                usersWhoLikedPropertyUnit[index + previewCount]
                            "
                        />
                    </v-col>
                </v-row>

                <v-row>
                    <v-col class="d-flex justify-center">
                        <v-btn
                            color="transparent"
                            @click="
                                showUsersWhoLikedPropertyUnit =
                                    !showUsersWhoLikedPropertyUnit
                            "
                            class="btn-edurent primary--text font-weight-regular"
                            depressed
                        >
                            View all
                            <v-icon>{{
                                    showUsersWhoLikedPropertyUnit
                                        ? "mdi-menu-up"
                                        : "mdi-menu-down"
                                }}
                            </v-icon>
                        </v-btn>
                    </v-col>
                </v-row>

                <v-row class="mt-4">
                    <v-col class="pb-0">
                        <h3>Amenities</h3>
                    </v-col>
                </v-row>
                <v-row v-if="featuredAmenities.length > 0" class="mb-4">
                    <v-col
                        cols="12"
                        sm="6"
                        md="4"
                        v-for="(n, i) in featuredAmenities.length"
                        :key="i"
                    >
                        <v-avatar size="16" tile>
                            <v-img
                                :alt="property.name ? property.name : ''"
                                max-height="16"
                                min-height="16"
                                max-width="16"
                                min-width="16"
                                contain
                                :src="
                                    featuredAmenities[i].icon_url
                                        ? featuredAmenities[i].icon_url
                                        : ''
                                "
                            >
                                <template slot="placeholder">
                                    <v-icon color="grey" class="lighten-5"
                                    >mdi-border-none-variant
                                    </v-icon
                                    >
                                </template>
                            </v-img>
                        </v-avatar>
                        <span class="pl-3">{{
                                featuredAmenities[i].name
                                    ? featuredAmenities[i].name
                                    : ""
                            }}</span>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col class="pb-0">
                        <h3>Property Amenities</h3>
                    </v-col>
                </v-row>
                <v-row v-if="propertyAmenities.length > 0" class="mb-4">
                    <v-col
                        cols="12"
                        sm="6"
                        md="4"
                        v-for="(n, i) in propertyAmenities.length"
                        :key="i"
                    >
                        <v-avatar size="16" tile>
                            <v-img
                                alt="Amenity"
                                max-height="16"
                                min-height="16"
                                max-width="16"
                                min-width="16"
                                contain
                                :src="
                                    propertyAmenities[i].amenity &&
                                    propertyAmenities[i].amenity.icon_url
                                        ? propertyAmenities[i].amenity.icon_url
                                        : ''
                                "
                            >
                                <template slot="placeholder">
                                    <v-icon color="grey" class="lighten-5"
                                    >mdi-border-none-variant
                                    </v-icon
                                    >
                                </template>
                            </v-img>
                        </v-avatar>
                        <span class="pl-3">{{
                                propertyAmenities[i].amenity &&
                                propertyAmenities[i].amenity.name
                                    ? propertyAmenities[i].amenity.name
                                    : ""
                            }}</span>
                    </v-col>
                </v-row>
                <v-row v-else>
                    <v-col>
                        <p class="no-data">Property does not have amenities</p>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col class="pt-10 pb-0">
                        <h3>About this property</h3>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col class="pb-10">
                        <p>
                            {{ property.description }}
                        </p>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col class="pb-0">
                        <h3>Community</h3>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col class="pb-10">
                        <p>
                            {{ property.community }}
                        </p>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col class="pb-0">
                        <h3>Location</h3>
                    </v-col>
                </v-row>
                <v-row class="mb-6">
                    <v-col>
                        <PropertyLocation :property="property" />
                    </v-col>
                </v-row>

                <v-row class="mt-8">
                    <v-col class="pb-0">
                        <PropertyReviews
                            :property-id="property.id"
                            :property-name="property.name"
                        />
                    </v-col>
                </v-row>

            </v-col>
            <v-col id="contactProperty" cols="12" md="4">
                <ContactProperty
                    :class="
                        $vuetify.breakpoint.lgAndUp &&
                        $vuetify.breakpoint.height > 800 &&
                        scroll < 2300
                            ? 'fixed'
                            : ''
                    "
                    :property="property"
                />
            </v-col>
        </v-row>

        <v-row>
            <v-col class="pb-2">
                <h4>Similar Properties Nearby</h4>
            </v-col>
        </v-row>
        <v-row class="justify-start mb-10" v-if="similarProperties.length > 0">
            <v-col
                cols="12"
                sm="6"
                md="4"
                xl="3"
                v-for="n in similarProperties.length"
                :key="n"
            >
                <property-card
                    :property="similarProperties[n - 1]"
                ></property-card>
            </v-col>
        </v-row>
        <v-row class="mb-10" v-else>
            <v-col>
                <p class="no-data">No similar properties</p>
            </v-col>
        </v-row>
        <MobileContact :property="property" />
    </v-container>
</template>

<script>
import ContactProperty from "../components/ContactProperty";
import PropertyUnitBlock from "../components/PropertyUnitBlock";
import RoommateCard from "../components/RoommateCard";
import PropertyLocation from "../components/PropertyLocation";
import PropertyCard from "../components/PropertyCard";
import PropertyService from "../services/property.service";
import MobileContact from "../components/MobileContact";
import PropertyReviews from "../components/Reviews/PropertyReviews";
import EngagementService from "../services/engagement.service";
import LocationBreadcrumb from "../components/LocationBreadcrumb";

export default {
    name: "PropertyDetails",
    props: ["propertyUnitId", "propertySlug"],
    components: {
        RoommateCard,
        PropertyCard,
        ContactProperty,
        PropertyUnitBlock,
        PropertyLocation,
        MobileContact,
        PropertyReviews,
        LocationBreadcrumb
    },
    data: () => ({
        liked: false,
        colors: [
            "indigo",
            "warning",
            "pink darken-2",
            "red lighten-1",
            "deep-purple accent-4"
        ],
        property: [],
        similarProperties: [],
        showUsersWhoLikedPropertyUnit: false,
        previewCount: 2,
        scroll: 0,
        hover: true,
        hoverLike: false,
        showVirtualTour: false,
        /**
         * please read comment on isUserFavorited method
         */
        favoriteControlFlag: null,
        metaData: {},
        waitlistLoadingUnitId: null
    }),
    metaInfo() {
        return {
            title: this.property.name + " Student Rentals",
            description: this.property.description,
            image: this.property.image,
            url:
                location.protocol +
                "//" +
                location.host +
                window.location.pathname,
            location:
                this.property.address_line_1 +
                ", " +
                this.property.city +
                ", " +
                (this.property.state ? this.property.state.abbrev : "") +
                " " +
                this.property.zip_code,
            currency: "USD",
        }
    },
    computed: {
        propertyMetaData() {
            return {
                title: this.property.name + " Student Rentals",
                description: this.property.description,
                image: this.property.image,
                url:
                    location.protocol +
                    "//" +
                    location.host +
                    window.location.pathname,
                location:
                    this.property.address_line_1 +
                    ", " +
                    this.property.city +
                    ", " +
                    (this.property.state ? this.property.state.abbrev : "") +
                    " " +
                    this.property.zip_code,
                currency: "USD",
            }
        },
        propertyUnitsByBeds() {
            const propertyUnits = [];

            // Save all of the property units under 0
            propertyUnits.push(this.property.property_units);

            this.property.property_units.forEach((pUnit) => {
                // If the number of bedrooms is 0 we already have it saved
                if (pUnit.number_of_bedrooms === 0) return;

                if (propertyUnits[pUnit.number_of_bedrooms] === undefined) {
                    propertyUnits[pUnit.number_of_bedrooms] = [];
                }

                propertyUnits[pUnit.number_of_bedrooms].push(pUnit);
            });

            return propertyUnits;
        },
        usersWhoLikedPropertyUnit() {
            let users = [];
            let duplicateIds = [];
            if (
                !this.property.favorite_property_units ||
                !this.property.contacted_property_units
            ) {
                return users;
            }
            this.property.favorite_property_units.forEach((favPropUnit) => {
                if (
                    favPropUnit.user &&
                    !duplicateIds.includes(Number(favPropUnit.user.id))
                ) {
                    users.push(favPropUnit.user);
                    duplicateIds.push(favPropUnit.user.id);
                }
            });
            this.property.contacted_property_units.forEach((contPropUnit) => {
                if (
                    contPropUnit.user &&
                    !duplicateIds.includes(Number(contPropUnit.user.id))
                ) {
                    users.push(contPropUnit.user);
                    duplicateIds.push(contPropUnit.user.id);
                }
            });

            return users;
        },
        propertyAmenities() {
            return this.property.property_amenities;
        },
        rentSpecialText() {
            let text = "";
            if (!this.property.active_property_rent_specials || this.property.active_property_rent_specials.length === 0) {
                return "No rent specials yet for this property.";
            }

            this.property.active_property_rent_specials.forEach((prs) => {
                const appendPeriod =
                    prs.rent_special.content.charAt(
                        prs.rent_special.content.length - 1
                    ) !== "."
                        ? "."
                        : "";
                text += `${prs.rent_special.content}${appendPeriod} `;
            });

            return text;
        },
        isUserLoggedIn() {
            return this.$store.getters.isUserLoggedIn;
        },
        propertyImages() {
            if (this.property?.property_images && this.property.property_images.length) {
                return this.property.property_images;
            }

            return [
                {
                    image_url: 'https://placehold.co/400x200/EEE/31343C?text=No%20Image',
                }
            ];
        },
        currentDisplayedImage() {
            return this.property?.property_images?.length > 0
                ? this.property.property_images[0]
                : null
        },
        user() {
            return this.$store.getters.getUser;
        },
        isUserFavorited() {
            /**
             * This is here to prevent waiting for the server response to change the button styles
             * I know this looks kind of hacky but if we don't return the flow before checking the propertyUnit variable
             * the view will tie this method with all the calculations related to the propertyUnit variable, and the like button styles won't be updated
             * untill Vue rerender all components in the view that are binded to the propertyUnit variable.
             * So the logic here is, if this.favoriteControlFlag === 1 item is liked if this.favoriteControlFlag === 0 item is not liked
             * if this.favoriteControlFlag !== 1 and  this.favoriteControlFlag !== 0 then check the propertyUnit variable and redo or do all
             * the calculations.
             */
            if (this.favoriteControlFlag === 1) {
                return true;
            } else if (this.favoriteControlFlag === 0) {
                return false;
            }
            if (!this.isUserLoggedIn) {
                return false;
            }
            return this.property.liked;
        },
        favoriteIcon() {
            return this.isUserFavorited || this.hoverLike
                ? "mdi-heart"
                : "mdi-heart-outline";
        },
        color() {
            return this.isUserFavorited || this.hoverLike
                ? "#FF4141"
                : "rgba(0,0,0,0.75)";
        },
        numBedsLabel() {
            let sorted_units = this.property.property_units
                .slice()
                .sort((a, b) => {
                    return a.number_of_bedrooms - b.number_of_bedrooms;
                });
            if (sorted_units.length === 1) {
                return sorted_units[0].number_of_bedrooms;
            }
            return `from ${sorted_units[0].number_of_bedrooms} to ${
                sorted_units[sorted_units.length - 1].number_of_bedrooms
            }`;
        },
        numBathslabel() {
            let sorted_units = this.property.property_units
                .slice()
                .sort((a, b) => {
                    return a.number_of_baths - b.number_of_baths;
                });
            if (sorted_units.length === 1) {
                return sorted_units[0].number_of_baths;
            }
            return `from ${sorted_units[0].number_of_baths} to ${
                sorted_units[sorted_units.length - 1].number_of_baths
            }`;
        },
        featuredAmenities() {
            let featuredAmenities = [];
            this.propertyAmenities.forEach((pa) => {
                if (
                    pa.amenity &&
                    pa.amenity.featured &&
                    pa.amenity.featured.toLowerCase() === "yes"
                ) {
                    featuredAmenities.push(pa.amenity);
                }
            });

            return featuredAmenities;
        },
        minPrice() {
            let sorted_units = this.property.property_units
                .slice()
                .sort((a, b) => {
                    return a.price - b.price;
                });
            return Math.ceil(sorted_units[0].price);
        },
        maxPrice() {
            let sorted_units = this.property.property_units
                .slice()
                .sort((a, b) => {
                    return a.price - b.price;
                });
            return Math.ceil(sorted_units[sorted_units.length - 1].price);
        }
    },
    watch: {
        propertyUnitId() {
            this.init();
        }
    },
    methods: {
        async init() {
            const params =
                "?appendProperty" +
                "&appendPropertyAmenities" +
                "&appendPropertyUnits" +
                "&appendFavoritePropertyUnits" +
                "&appendPropertyUnitsAvailabilities" +
                "&appendFavoritedUsers" +
                "&appendActivePropertyRentSpecials" +
                "&appendPropertyImages" +
                "&appendPropertyUnitAmenities" +
                "&appendLocationHierarchy" +
                "&includeLocationCampuses";
            if (this.isUserLoggedIn) {
                this.property = await PropertyService.getProperty(
                    this.propertyUnitId,
                    params
                );
            } else {
                this.property = await PropertyService.getPublicProperty(
                    this.propertyUnitId,
                    params
                );
            }
            this.similarProperties = await PropertyService.getSimilarProperties(
                this.propertyUnitId
            );

            // Track detail view for the first image
            if (this.currentDisplayedImage) {
                EngagementService.trackDetailView(
                    this.currentDisplayedImage.id
                );
            }
        },
        openSignIn() {
            this.$store.dispatch("setSignInModal", true);
        },
        async goToApplicationWebsite(propertyUnitId) {
            if (this.isUserLoggedIn) {
                // This is a workaround for safari blocking pop-ups.
                let newWindow = window.open("about:blank", "_blank");
                let website = this.property.application_website;
                await PropertyService.referStudentToProperty(this.user.id, {
                    data: {
                        propertyId: propertyUnitId,
                        includeRoommates: true,
                        sender: "student"
                    }
                }).then(function() {
                    newWindow.location = website;
                });
            } else {
                this.openSignIn();
            }
        },
        async joinWaitlist(propertyUnitId) {
            if (this.isUserLoggedIn) {
                try {
                    this.waitlistLoadingUnitId = propertyUnitId;

                    await PropertyService.joinWaitlist(propertyUnitId);
                    await this.$store.dispatch("getUserByToken");
                } catch (e) {
                    await this.$store.commit("setSnackbar", {
                        show: true,
                        message:
                            "Something went wrong while adding you to the waitlist. Please try again.",
                        color: "error"
                    });
                } finally {
                    this.waitlistLoadingUnitId = null;
                }
            } else {
                this.openSignIn();
            }
        },
        async removeFromWaitlist(propertyUnitId) {
            if (this.isUserLoggedIn) {
                try {
                    this.waitlistLoadingUnitId = propertyUnitId;

                    await PropertyService.removeFromWaitlist(propertyUnitId);
                    await this.$store.dispatch("getUserByToken");
                } catch (e) {
                    await this.$store.commit("setSnackbar", {
                        show: true,
                        message:
                            "Something went wrong while removing you from the waitlist. Please try again.",
                        color: "error"
                    });
                } finally {
                    this.waitlistLoadingUnitId = null;
                }
            } else {
                this.openSignIn();
            }
        },
        handleLike: async function() {
            if (!this.isUserLoggedIn) {
                this.$store.dispatch("setSignInModal", true);
                return;
            }
            this.$store.dispatch("setSelectUnitToggleLikeModal", true);
            this.$store.dispatch("setPropertyToLike", this.property);
        },
        handleScroll() {
            this.scroll = window.scrollY;
        }
    },
    async beforeMount() {
        await this.init();

        if (this.isUserLoggedIn) {
            this.$store.dispatch("getFavoriteRoommates");
        }

        const nameElement = document.querySelector("[name='description']");
        if (nameElement !== null) nameElement.remove();

        this.metaData = this.propertyMetaData;
    },
    created() {
        window.addEventListener("scroll", this.handleScroll);
        this.unsubscribe = this.$store.subscribe((mutation, state) => {
            if (
                mutation.type === "setSelectUnitToggleLikeModalState" &&
                state.Site.showSelectUnitToggleLikeModal === false
            ) {
                this.init();
            }
        });
    },
    beforeDestroy() {
        this.unsubscribe();
    },
    destroyed() {
        window.removeEventListener("scroll", this.handleScroll);
        // Removes facebook og meta tags.
        const ogElement = document.querySelector("[property^='og:']");
        if (ogElement !== null) ogElement.remove();
    }
};
</script>

<style scoped>
.fixed {
    position: fixed;
    left: 70vw;
    top: 10%;
    z-index: 5;
}

.property-details-title {
    font-size: 30px !important;
    font-weight: 700 !important;
}

.property-photos {
    position: relative;
}

.property-photos .v-carousel {
    border-radius: 10px !important;
}

.property-photos .extra {
    position: absolute;
    top: 35px;
    right: 35px;
    z-index: 1;
    display: flex;
    gap: 8px;
}

.property-detail .properties-name-price {
    font-size: 30px !important;
    font-weight: 700;
    line-height: 30px;
}

.property-detail .properties-details {
    font-size: 16px;
    line-height: 21px;
}

.rent-specials-box .v-btn {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
}

.rent-special-item {
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 12px;
}

.rent-special-item:last-child {
    border-bottom: none;
    padding-bottom: 0;
}

.rent-special-offer {
    font-size: 18px;
    color: #2d76e3;
    display: block;
    margin-bottom: 4px;
}

.rent-special-description {
    font-size: 14px;
    color: #666;
}

.availability-box h4 {
    font-size: 20px !important;
    font-weight: 700;
}

.availability-box .no-property {
    height: 90px;
    font-size: 14px !important;
    font-style: italic;
}

.property-detail h3 {
    font-size: 20px !important;
    font-weight: 700;
    color: #000;
}

.property-detail h5 {
    font-size: 16px !important;
    font-weight: 700;
    color: #000;
}

.property-detail .no-data {
    font-size: 14px !important;
    font-style: italic;
    opacity: 0.5;
}

.property-detail .no-rounded {
    border-radius: 0 !important;
}

.price-label {
    font-size: 14px;
    font-weight: 400;
    color: #666;
}

.property-photos .extra {
    display: flex;
    gap: 8px;
}

.virtual-tour-dialog {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: #000;
}

.virtual-tour-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 8px;
    background: rgba(0, 0, 0, 0.85);
    color: #fff;
    min-height: 36px;
}

.virtual-tour-title {
    font-size: 13px;
    font-weight: 500;
    display: flex;
    align-items: center;
    opacity: 0.9;
}

.virtual-tour-iframe-container {
    flex: 1;
    background: #000;
}

.virtual-tour-iframe-container iframe {
    display: block;
}
</style>
